import { buildStory, type StoryCountdownDays } from 'fym-shared/src/music24/story/storyCopy';
import type { StoryFormatId } from 'fym-shared/src/music24/story/storyFormats';
import {
  storyStyleOf,
  type KitStory,
  type KitStoryMoment,
  type StoryStyle,
} from 'fym-shared/src/music24/story/storyTypes';
import type { KitTextLocale } from 'fym-shared/src/music24/kitText';
import type { StudioMoment } from 'fym-shared/src/music24/studioThemes';

/**
 * The Story layout of a Studio moment: on a Story "Countdown" (`announce`) makes the day stills; on
 * a feed post `announce` is its own post ("New single" and the date ring, design e).
 */
export function storyMomentOfStudio(
  moment: StudioMoment | undefined,
  releaseDate: string | null,
  today: string,
  format: StoryFormatId = 'story'
): KitStoryMoment {
  switch (moment) {
    case 'preSave':
      return 'preSave';
    case 'lyricLine':
      return 'lyric';
    case 'announce':
      return format === 'story' ? 'countdown' : 'announce';
    case 'outNow':
      return 'outNow';
    case undefined:
      // The server rule (`assetMoment`): pre-save before the release, out now after it.
      return releaseDate !== null && releaseDate.slice(0, 10) > today.slice(0, 10) ? 'preSave' : 'outNow';
    default: {
      const exhaustive: never = moment;
      return exhaustive;
    }
  }
}

/** The first lyric line of the song: no section label, no blank line. */
function firstLyricLine(lyrics: string | null): string | null {
  const line = (lyrics ?? '')
    .split(/\r?\n/)
    .map((part) => part.trim())
    .find((part) => part !== '' && !/^\[.*\]$/.test(part));
  return line ?? null;
}

export interface StudioStoryInput {
  /** The frame: a Story (absent) or a feed post of the picked shape. */
  format?: StoryFormatId;
  moment: StudioMoment | undefined;
  /** The picked lyric (one line, or two lines with a line break). */
  lyricLine?: string;
  /** The countdown day the preview shows: the first day still to come, else 7. */
  countdownDays?: StoryCountdownDays | null;
  title: string;
  artist: string;
  /** `YYYY-MM-DD`; null with no date. */
  releaseDate: string | null;
  lyrics: string | null;
  /** The SmartLink address of the Story (the release kit answer `story.link`); null: none. */
  link: string | null;
  /** The song has a published pre-save page (else the pill of a Story or a feed post says "Coming soon"). */
  presave?: boolean;
  withLogo: boolean;
  /** Today as `YYYY-MM-DD` (UTC), like the server. */
  today: string;
  /** The language of the kit words (the release kit answer `story.locale`); absent: English. */
  locale?: KitTextLocale;
}

/**
 * The texts of the Story (or feed post) preview: the one builder of the render (fym-shared `buildStory`), with the
 * Studio choices. The server checks the lyric against the song (`storyLyricOf`); the Studio picks
 * only lines of the song, so the preview takes the pick as it is. Pure.
 */
export function studioStory(input: StudioStoryInput): KitStory {
  const format = input.format ?? 'story';
  const moment = storyMomentOfStudio(input.moment, input.releaseDate, input.today, format);
  const picked = input.lyricLine?.trim() ?? '';
  return buildStory({
    ...(format === 'story' ? {} : { format }),
    // A Story and a feed post of a song with no published pre-save page say "Coming soon".
    presave: input.presave ?? true,
    moment,
    title: input.title,
    artist: input.artist,
    releaseDate: input.releaseDate,
    today: input.today,
    countdownDays: input.countdownDays ?? null,
    lyric: picked !== '' ? picked : firstLyricLine(input.lyrics),
    link: input.link,
    withLogo: input.withLogo,
    locale: input.locale,
  });
}

/** The Story style of the Studio style pick (the server maps the same ids). */
export function studioStoryStyle(assetStyle: string | null | undefined): StoryStyle {
  return storyStyleOf(assetStyle);
}

/** Today as `YYYY-MM-DD` in UTC. */
export function utcToday(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}
